"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { ChevronDown, Globe, MapPin, CheckCircle, ArrowRight } from "lucide-react";
import { RegionData, RegionCountry } from "../services/regions.api";
import Link from "next/link";

interface RegionsPageContentProps {
  regions: RegionData[];
  currentRegionCode: string | null;
}

// Helper to convert ISO 3166-1 alpha-2 country code to emoji flag
function getFlagEmoji(countryCode: string): string {
  if (!countryCode) return "🌐";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  try {
    return String.fromCodePoint(...codePoints);
  } catch {
    return "🌐";
  }
}

export default function RegionsPageContent({ regions, currentRegionCode }: RegionsPageContentProps) {
  const locale = useLocale();
  const router = useRouter();
  const t = useTranslations("web.regionsPage");
  
  const handleSelectCountry = (regionSlug: string) => {
    // Redirect to /[regionSlug]/[locale]
    router.push(`/${regionSlug}/${locale}`);
  };

  const getLocalizedName = (name: string | null | undefined) => name ?? "";

  return (
    <div className="w-full bg-[#f8fafc] min-h-screen text-primary pb-16 font-sans">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-[#083E8F] to-secondary text-white py-16 md:py-24 px-6">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/10 to-transparent pointer-events-none" />
        <div className="max-w-[1200px] mx-auto text-center relative z-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-white text-sm font-semibold tracking-wide backdrop-blur-md border border-white/10 mb-6">
            <Globe className="w-4 h-4 animate-spin-slow" />
            {t("selectRegion")}
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6 drop-shadow-sm">
            {t("title")}
          </h1>
          <p className="max-w-2xl mx-auto text-white/95 text-base md:text-lg leading-relaxed font-light font-sans">
            {t("subtitle")}
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-[1000px] mx-auto px-6 mt-12">
        {regions.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-md border border-slate-100">
            <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-lg text-slate-500 font-medium">{t("noRegions")}</p>
            <Link 
              href={`/${locale}`}
              className="inline-flex items-center gap-2 mt-6 bg-secondary hover:bg-primary text-white px-6 py-3 rounded-full font-semibold transition"
            >
              {t("backToHome")}
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {regions.map((region) => {
              const regionName = getLocalizedName(region.name);
              const isCurrentRegion = region.slug?.toLowerCase() === currentRegionCode?.toLowerCase();

              return (
                <div 
                  key={region.id} 
                  className={`bg-white rounded-3xl border border-slate-100 transition-all duration-300 p-6 md:p-8 flex flex-col gap-6 shadow-sm hover:shadow-md ${
                    isCurrentRegion ? "border-secondary ring-4 ring-secondary/5" : ""
                  }`}
                >
                  {/* Region Select Header (Clickable) */}
                  <div
                    onClick={() => handleSelectCountry(region.slug)}
                    className="flex items-center gap-4 cursor-pointer group"
                  >
                    <div className={`p-3 rounded-2xl transition-colors ${
                      isCurrentRegion ? "bg-secondary/10 text-secondary" : "bg-slate-100 text-slate-500 group-hover:bg-secondary/10 group-hover:text-secondary"
                    }`}>
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h3 className="text-xl font-bold text-primary group-hover:text-secondary transition-colors flex items-center gap-2">
                          <span>{regionName}</span>
                          <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${
                            locale === "ar" 
                              ? "rotate-180 group-hover:-translate-x-1" 
                              : "group-hover:translate-x-1"
                          } ${
                            isCurrentRegion 
                              ? "text-secondary" 
                              : "text-slate-400 group-hover:text-secondary"
                          }`} />
                        </h3>
                        {isCurrentRegion && (
                          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-medium border border-emerald-100">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            {t("active")}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {region.countries.length} {t("countries")}
                      </p>
                    </div>
                  </div>

                  {/* Static list of countries (no link/hover cursor) */}
                  {region.countries.length > 0 && (
                    <div className="border-t border-slate-100 pt-5">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
                        {t("countries")}
                      </h4>
                      <div className="flex flex-wrap gap-2.5">
                        {region.countries.map((country) => {
                          const countryName = getLocalizedName(country.name);
                          return (
                            <div
                              key={country.id}
                              className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-slate-50 border border-slate-100 rounded-full text-sm text-primary select-none"
                            >
                              <span className="text-lg filter drop-shadow-sm">
                                {getFlagEmoji(country.code)}
                              </span>
                              <span className="font-medium">{countryName}</span>
                              {country.currency_code && (
                                <span className="text-xs text-slate-400 bg-white border border-slate-100 px-1.5 py-0.5 rounded-md font-mono">
                                  {country.currency_code}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
